/**
 * UI-тексты страницы достопримечательности (PoiPageContent).
 * Общие надзаголовки/теги (FAQ, «Популярные вопросы») берутся из servicePage (sp.*);
 * здесь — только вертикаль-специфичные строки. Динамические — функции.
 * Выбор склонённого значения и формат чисел остаются в компоненте.
 */
export const poiPageUi = {
    en: {
        notFound: 'Page not found',
        attraction: 'Attraction',
        home: 'Home',

        // Weekday abbreviations for the full opening-hours schedule
        weekdays: { mon: 'Mon', tue: 'Tue', wed: 'Wed', thu: 'Thu', fri: 'Fri', sat: 'Sat', sun: 'Sun' },

        // Overview/CTA card actions
        buyTickets: 'Buy tickets online',
        officialSite: 'Official website',
        hours: 'Opening hours',
        today: 'Today',
        open: 'Open now',
        closed: 'Closed',
        showSchedule: 'Full schedule',
        hide: 'Hide',
        openMaps: 'Open in Google Maps',
        directions: 'Directions',
        heroDirections: 'How to get there',
        heroBoatTrip: 'Find a boat trip',
        heroNearbyTour: 'Find a nearby tour',

        // Sidebar meta
        entry: 'Entry',
        entryPaid: 'Paid',
        entryFree: 'Free',

        // Section eyebrows / titles
        aboutEyebrow: 'About',
        transportEyebrow: 'Transport',
        travelTime: 'Travel time',
        // Travel-time fact cards (top strip); city arrives pre-declined
        travelCards: {
            walk: (city) => `On foot from ${city} center`,
            byCar: 'By car from',
            airport: 'From the airport',
            selectCity: 'Choose departure city',
            selectAirport: 'Choose airport',
        },
        mapEyebrow: 'On the map',
        mapTitle: 'Location',
        descEyebrow: 'In detail',
        descTitle: "What's here",
        highlightsEyebrow: 'Highlights',
        highlightsTitle: 'Why visit',
        historyEyebrow: 'History',
        historyTitle: 'Past & present',
        visitingEyebrow: 'For visitors',
        visitingTitle: 'Visitor information',
        ticketsCard: 'Tickets & prices',
        rulesCard: 'Rules & restrictions',
        facilitiesCard: 'On-site facilities',
        gettingEyebrow: 'Getting there',
        gettingTitle: 'Transport & directions',
        bestTimeEyebrow: 'When to go',
        bestTimeTitle: 'Best time to visit',

        // FAQ lead + share
        faqLead: 'Practical answers about visiting, routes and time on site.',
        share: 'Share',

        // Anchors (vertical-specific; FAQ anchor reuses sp.anchor.faq)
        anchorAbout: 'About',
        anchorTravel: 'Travel time',
        anchorMap: 'On the map',
        anchorHighlights: 'Highlights',
        anchorHistory: 'History',
        anchorVisiting: 'For visitors',
        anchorGetting: 'Getting there',
        anchorBestTime: 'When to go',
        anchorNearby: 'Nearby',

        // Bottom CTA (interpolated with city name)
        ctaEyebrow: (city) => `${city} — everything you need for your trip`,
        ctaTitle: 'Routes, attractions, transport and more',
        ctaBtn: 'Learn more',

        // Nearby
        nearbyEyebrow: 'Nearby',
        nearbyTitle: 'Nearby attractions',
        nearbyAll: 'All attractions',
        distMeters: (m) => `${m} m`,
        distKm: (km) => `${km} km`,
    },
    ru: {
        notFound: 'Страница не найдена',
        attraction: 'Достопримечательность',
        home: 'Главная',

        weekdays: { mon: 'Пн', tue: 'Вт', wed: 'Ср', thu: 'Чт', fri: 'Пт', sat: 'Сб', sun: 'Вс' },

        buyTickets: 'Купить билет онлайн',
        officialSite: 'Официальный сайт',
        hours: 'Часы работы',
        today: 'Сегодня',
        open: 'Открыто сейчас',
        closed: 'Закрыто',
        showSchedule: 'Полное расписание',
        hide: 'Скрыть',
        openMaps: 'Открыть в Google Maps',
        directions: 'Маршрут',
        heroDirections: 'Как добраться',
        heroBoatTrip: 'Морские прогулки и туры',
        heroNearbyTour: 'Найти экскурсию рядом',

        entry: 'Вход',
        entryPaid: 'Платный',
        entryFree: 'Свободный',

        aboutEyebrow: 'О месте',
        transportEyebrow: 'Транспорт',
        travelTime: 'Время в пути',
        travelCards: {
            walk: (city) => `Пешком из центра ${city}`,
            byCar: 'На машине из',
            airport: 'Из аэропорта',
            selectCity: 'Выбрать город отправления',
            selectAirport: 'Выбрать аэропорт',
        },
        mapEyebrow: 'На карте',
        mapTitle: 'Где находится',
        descEyebrow: 'Подробно',
        descTitle: 'Описание',
        highlightsEyebrow: 'Главное',
        highlightsTitle: 'Зачем сюда идти',
        historyEyebrow: 'История',
        historyTitle: 'Прошлое и настоящее',
        visitingEyebrow: 'Для посетителей',
        visitingTitle: 'Полезная информация',
        ticketsCard: 'Билеты и цены',
        rulesCard: 'Правила и ограничения',
        facilitiesCard: 'Инфраструктура',
        gettingEyebrow: 'Как добраться',
        gettingTitle: 'Транспорт и маршруты',
        bestTimeEyebrow: 'Когда идти',
        bestTimeTitle: 'Лучшее время для посещения',

        faqLead: 'Практические ответы о посещении, маршруте и времени на месте.',
        share: 'Поделиться',

        anchorAbout: 'О месте',
        anchorTravel: 'Время в пути',
        anchorMap: 'На карте',
        anchorHighlights: 'Главное',
        anchorHistory: 'История',
        anchorVisiting: 'Для посетителей',
        anchorGetting: 'Как добраться',
        anchorBestTime: 'Когда идти',
        anchorNearby: 'Рядом',

        ctaEyebrow: (city) => `${city} - всё что необходимо для поездки`,
        ctaTitle: 'Маршруты, достопримечательности, транспорт и не только',
        ctaBtn: 'Подробнее',

        nearbyEyebrow: 'Рядом',
        nearbyTitle: 'Ближайшие достопримечательности',
        nearbyAll: 'Все достопримечательности',
        distMeters: (m) => `${m} м`,
        distKm: (km) => `${km} км`,
    },
};
